import moment from 'moment';
import React, {Component} from 'react';
import {createFragmentContainer, graphql} from 'react-relay';
import {withRouter} from 'react-router-dom';
import {MODULE_TYPES, SOCKET_ACTION, SOCKET_EVENT_TYPE, TIERS} from '../constants';
import SlideoutConductor from '../forecast-app/shared/components/slideout/SlideoutConductor';
import {setAvailableMLModels} from '../forecast-app/shared/util/AvailableMLModelUtil';
import {setTimeRegistrationSettings} from '../forecast-app/shared/util/cache/TimeRegistrationSettingsUtil';
import {setCompanySetup} from '../forecast-app/shared/util/CompanySetupUtil';
import {setAvailableFeatureFlags} from '../forecast-app/shared/util/FeatureUtil';
import {setProjectStages} from '../forecast-app/shared/util/ProjectStageCache';
import {hasModule, setEnabledModules} from '../forecast-app/shared/util/ModuleUtil';
import {hasPermission, setPermissions} from '../forecast-app/shared/util/PermissionsUtil';
import {claimRecentHistory} from '../forecast-app/shared/util/RecentHistoryUtil';
import Util from '../forecast-app/shared/util/util';
import {PERMISSION_TYPE} from '../Permissions';
import {withSocketHandling} from '../socket/withSocketHandling';

import {ForecastThemeProvider} from '../forecast-app/shared/ForecastThemeProvider';
import AccessBlockedContent from './AccessBlockedContent';

const ACCESS_BLOCKED_PATH = '/access-blocked';

const cacheProjectStages = company => {
	const stages = (company?.projectStages?.edges ?? []).flatMap(edge => (edge?.node ? [edge.node] : []));
	setProjectStages(
		stages.map(({__typename, id, name, sortOrder, profile}) => ({
			__typename,
			id,
			name,
			sortOrder,
			profile: profile ?? null,
		}))
	);
};

class appv2 extends Component {
	constructor(props) {
		super(props);

		Util.setCsrfValue(props.viewer.csrfToken);
		setPermissions(props.viewer.permissions);
		setAvailableFeatureFlags(props.viewer.availableFeatureFlags);
		cacheProjectStages(props.viewer.company);
		setAvailableMLModels(props.viewer.company.availableMLModels);
		setEnabledModules(props.viewer.company.modules);
		setCompanySetup(props.viewer.company);
		setTimeRegistrationSettings(props.viewer.company.timeRegistrationSettings);
		claimRecentHistory(props.viewer.actualPersonId);
	}

	UNSAFE_componentWillMount() {
		if (this.props.viewer.downForMaintenance) {
			this.props.history.push({
				pathname: '/maintenance',
			});
			return;
		}

		if (this.props.viewer.actualPersonId !== null) {
			const isTrial = this.props.viewer.company?.tier === TIERS.TRIAL;
			const isOnTrialEnd = this.props.history.location.pathname === '/trial-end';
			const trialEndTime = moment.utc(this.props.viewer.company.trialEndTime);
			const now = moment.utc();
			if (isTrial && !isOnTrialEnd && trialEndTime.isBefore(now)) {
				this.props.history.push('/trial-end');
			}
		}

		this.props.setLocale && this.props.setLocale(this.props.viewer.language);
	}

	componentDidMount() {
		if (this.props.viewer.actualPersonId === null) {
			this.props.history.push({
				pathname: '/login',
			});
		} else if (!this.props.viewer.hasAcceptedTerms) {
			this.props.history.push({
				pathname: '/accept-terms',
				state: {nextPathname: this.props.location.pathname},
			});
		} else if (this.isAccessBlocked()) {
			this.enforceAccessBlockedUrl();
		}

		const socketEvents = [
			{
				type: SOCKET_EVENT_TYPE.MODULE,
				action: SOCKET_ACTION.UPDATE,
			},
			{
				type: SOCKET_EVENT_TYPE.FEATURE_FLAG,
				action: SOCKET_ACTION.UPDATE,
				personIds: [this.props.viewer.backendId],
			},
			{
				type: SOCKET_EVENT_TYPE.PERMISSION,
				action: SOCKET_ACTION.UPDATE,
				personIds: [this.props.viewer.backendId],
			},
			{
				type: SOCKET_EVENT_TYPE.TIME_REGISTRATION_SETTINGS,
				action: SOCKET_ACTION.UPDATE,
			},
		];
		this.props.setSocketConfig(socketEvents);
	}

	componentDidUpdate(prevProps) {
		if (this.props.viewer.actualPersonId === null) {
			// Check necessary to prevent infinite update
			if (!this.props.location.state || this.props.location.state.nextPathname !== '/login') {
				this.props.history.push({
					pathname: '/login',
					state: {nextPathname: this.props.location.pathname},
				});
			}
		} else if (!this.props.viewer.hasAcceptedTerms) {
			// Terms acceptance outranks the block, the same way it does on mount. Enforcing the blocked url
			// here would replace a pending /accept-terms redirect and leave the person unable to accept.
		} else if (this.isAccessBlocked()) {
			// Only while someone is logged in: logging out nulls actualPersonId, and enforcing the blocked
			// url for a logged-out person would clobber the redirect to /login above and pin them in the app.
			this.enforceAccessBlockedUrl();
		}

		setPermissions(this.props.viewer.permissions);
		setAvailableFeatureFlags(this.props.viewer.availableFeatureFlags);
		cacheProjectStages(this.props.viewer.company);
		setEnabledModules(this.props.viewer.company?.modules);
		setCompanySetup(this.props.viewer.company);
		setTimeRegistrationSettings(this.props.viewer.company?.timeRegistrationSettings);
		claimRecentHistory(this.props.viewer.actualPersonId);

		// Redirect to sage subdomain if needed
		if (hasModule(MODULE_TYPES.SAGE_PSA_RESTRICTED)) {
			if (window.location.hostname === 'testv2.wolwa.com') {
				window.location.href = `https://sage.wolwa.com${window.location.pathname}`;
			} else if (window.location.hostname === 'app.forecast.it') {
				window.location.href = `https://sage.forecast.it${window.location.pathname}`;
			}
		}
	}

	isAccessBlocked() {
		return !!this.props.viewer?.company?.accessBlocked;
	}

	/**
	 * Keeps the address bar honest while the block is in force. The block itself is enforced in render(),
	 * not here — a redirect alone is not a blocker, because an in-app route change never remounts this
	 * component and so never re-runs a mount-time check.
	 */
	enforceAccessBlockedUrl() {
		if (this.props.history.location.pathname !== ACCESS_BLOCKED_PATH) {
			this.props.history.replace(ACCESS_BLOCKED_PATH);
		}
	}

	render() {
		if (this.props.viewer.actualPersonId === null || this.props.viewer.company === null) {
			return <div />;
		}

		// A blocked company gets the blocked page and nothing else — no header, no navigation, no routed
		// page, not even the subscription page. render() runs again on every route change, so there is no
		// window in which another page can appear.
		if (this.isAccessBlocked()) {
			return (
				<ForecastThemeProvider>
					<AccessBlockedContent />
				</ForecastThemeProvider>
			);
		}

		const isAdmin = hasPermission(PERMISSION_TYPE.MANAGE_ACCOUNT_SETTINGS);
		const isTrial = this.props.viewer.company.tier === TIERS.TRIAL;
		const isOnTrialEnd = this.props.history.location.pathname === '/trial-end';
		const trialEndTime = this.props.viewer.company.trialEndTime;

		const buyNowTime = isAdmin && isTrial && !isOnTrialEnd && trialEndTime;

		return (
			<ForecastThemeProvider>
				{this.props.appContent(
					this.props.viewer.actualPersonId,
					this.props.viewer.availableFeatureFlags,
					this.props.viewer.company.modules,
					buyNowTime,
					this.props.viewer.company.tier,
					this.props.viewer.company.id
				)}
				<SlideoutConductor />
			</ForecastThemeProvider>
		);
	}
}

const appv2Query = graphql`
	query appv2_Query {
		viewer {
			actualPersonId
			component(name: "appv2")
			...appv2_viewer
		}
	}
`;

// The unwrapped class, exported for tests only. App code must use the default export, which is the
// class inside its fragment, router and socket wrappers.
export {appv2Query, appv2 as UnwrappedAppV2};

export default createFragmentContainer(withSocketHandling(withRouter(appv2)), {
	viewer: graphql`
		fragment appv2_viewer on Viewer {
			id
			backendId
			csrfToken
			actualPersonId
			permissions
			hasAcceptedTerms
			downForMaintenance
			availableFeatureFlags {
				key
			}
			email
			language
			company {
				id
				projectStages(first: 1000) @connection(key: "Company_projectStages") {
					edges {
						node {
							__typename
							id
							name
							sortOrder
							profile
						}
					}
				}
				accessBlocked
				trialEndTime
				tier
				modules {
					endYear
					endMonth
					endDay
					moduleType
				}
				availableMLModels {
					lowHighModel
					overrunModel
					roleModel
					assigneeModel
					labelModel
				}
				timeRegistrationSettings {
					granularity
					allowDoneTasks
					allowProjects
					allowInternalTime
					allowBillableSplit
					allowRoles
					allowExceedingEstimate
					allowOutsideTaskDates
					allowOutsideProjectDates
					requireTaskAssignment
					requireNote
				}
				revenueEnabled
				timesheetsEnabled
				clientsEnabled
				weekendDisplayPerUser
				weekendDisplayShowAlways
			}
		}
	`,
});
